// Batched Contractor & Commission pay runs: group approved payments into a run,
// then validate → approve (dual control) → lock → pay → reconcile, with a
// balanced WHT GL journal. Reuses the payroll approval + audit patterns.
// A run pays ONE company (multi-entity Phase 5): it is created for a company
// the person can reach, and takes only the lines they tick from that
// company's approved lines in its currency.
import { ContractorAccountingPanel } from './ContractorAccountingPanel';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { commissionApi, type CommissionRun, type CommissionLine, type GLJournal } from '../api/commissionApi';
import { listVisibleCompanies, type OverviewCompany } from '@/features/payroll/api/groupOverview';
import { getApiErrorMessage } from '@/lib/apiError';

const RUN_STATUS: Record<string, 'secondary' | 'warning' | 'info' | 'success' | 'danger'> = {
  draft: 'secondary', validated: 'info', approved: 'warning', locked: 'warning',
  payment_pending: 'info', paid: 'success', failed: 'danger', voided: 'secondary',
};
const money = (v: string, ccy: string) => `${ccy} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// The single contextual action for a run's current status.
const NEXT: Record<string, { action: 'validate' | 'approve' | 'lock' | 'pay' | 'reconcile'; label: string; approver?: boolean }> = {
  draft: { action: 'validate', label: 'Validate' },
  validated: { action: 'approve', label: 'Approve', approver: true },
  approved: { action: 'lock', label: 'Lock', approver: true },
  locked: { action: 'pay', label: 'Pay', approver: true },
  payment_pending: { action: 'reconcile', label: 'Reconcile' },
  failed: { action: 'pay', label: 'Retry pay', approver: true },
};

// The line picker for one run: null while loading.
type Picker = { runId: string; lines: CommissionLine[] | null; withheld: boolean; chosen: Record<string, boolean> };

export function RunsPanel({ canApprove, onChanged }: { canApprove: boolean; onChanged: () => void }) {
  const [runs, setRuns] = useState<CommissionRun[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nr, setNr] = useState({ name: '', country_code: 'TZ', currency: 'TZS', legal_entity_id: '' });
  // Companies the person can see (reach-filtered server-side). null = not
  // loaded or failed: the form then can't offer a company, and says so.
  const [companies, setCompanies] = useState<OverviewCompany[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [gl, setGl] = useState<GLJournal | null>(null);
  const [picker, setPicker] = useState<Picker | null>(null);
  // Chosen lines the server didn't add (they changed or joined another run),
  // shown on the run itself until the next add.
  const [skipped, setSkipped] = useState<{ runId: string; count: number } | null>(null);

  const refresh = async () => { try { setRuns(await commissionApi.listRuns()); } catch { setRuns([]); } };
  useEffect(() => {
    refresh();
    listVisibleCompanies().then(setCompanies).catch(() => setCompanies(null));
  }, []);
  const companyName = (id?: string | null) => (id ? companies?.find((c) => c.id === id)?.name ?? 'Company not visible to you' : null);

  // Picking a company fills its country and currency (still editable).
  const pickCompany = (id: string) => {
    const c = companies?.find((x) => x.id === id);
    setNr((v) => ({ ...v, legal_entity_id: id, country_code: c?.country || v.country_code, currency: c?.base_currency || v.currency }));
  };
  const openCreate = () => {
    if (!creating && !nr.legal_entity_id && companies?.length === 1) pickCompany(companies[0].id);
    setCreating(!creating);
  };

  const create = async () => {
    if (!nr.legal_entity_id) { toast.error('Choose the company this run pays for'); return; }
    if (!nr.name) { toast.error('Name the run'); return; }
    setBusy('new');
    try { await commissionApi.createRun(nr); setCreating(false); setNr({ ...nr, name: '' }); await refresh(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Could not create run')); }
    finally { setBusy(null); }
  };

  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try { await fn(); toast.success(ok); await refresh(); onChanged(); }
    catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Action failed'));
      // A refused action can still have changed the run (an incomplete pay
      // run sent some payments), so always show its current state.
      await refresh(); onChanged();
    }
    finally { setBusy(null); }
  };

  // Add approved: list the lines the server would take for this run, and add
  // only the ones the person ticks.
  const openPicker = async (run: CommissionRun) => {
    if (picker?.runId === run.id) { setPicker(null); return; }
    setPicker({ runId: run.id, lines: null, withheld: false, chosen: {} });
    try {
      const r = await commissionApi.eligibleLines(run.id);
      setPicker({ runId: run.id, lines: r.lines, withheld: !!r.lines_withheld, chosen: {} });
    } catch (e: any) {
      setPicker(null);
      toast.error(getApiErrorMessage(e, 'Could not load the lines for this run'));
    }
  };
  const addChosen = (run: CommissionRun) => {
    const ids = Object.keys(picker?.chosen ?? {}).filter((id) => picker?.chosen[id]);
    if (!ids.length) return; // the Add button is disabled until a line is ticked
    setSkipped(null);
    return act(run.id, async () => {
      const r = await commissionApi.attachLines(run.id, ids);
      setPicker(null);
      if (r.skipped) setSkipped({ runId: run.id, count: r.skipped });
    }, 'Lines added');
  };

  const showGL = async (id: string) => {
    setExpanded(expanded === id ? null : id);
    if (expanded !== id) { try { setGl(await commissionApi.getRunGL(id)); } catch { setGl(null); } }
  };

  if (runs === null) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Layers className="h-4 w-4" /> Pay runs</div>
          <Button size="sm" variant="outline" onClick={openCreate}>New pay run</Button>
        </div>
        {creating && (
          <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-3">
            {companies === null || companies.length === 0 ? (
              <p className="w-full text-sm text-warning">
                {companies === null ? 'Your companies could not be loaded, so a run can’t be created. Try again.' : 'You don’t have access to any company to pay contractors for.'}
              </p>
            ) : (
              <select aria-label="Company" value={nr.legal_entity_id} onChange={(e) => pickCompany(e.target.value)}
                className="h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground">
                <option value="">Choose the company…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <Input placeholder="Run name (e.g. July agents)" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} className="w-56" />
            <Input placeholder="Country" value={nr.country_code} onChange={(e) => setNr({ ...nr, country_code: e.target.value })} className="w-20" />
            <Input placeholder="Currency" value={nr.currency} onChange={(e) => setNr({ ...nr, currency: e.target.value })} className="w-24" />
            <Button size="sm" onClick={create} disabled={busy === 'new' || !nr.legal_entity_id}>Create</Button>
          </div>
        )}
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pay runs yet. Create one to batch approved contractor payments.</p>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const next = NEXT[run.status];
              const canDoNext = next && (!next.approver || canApprove);
              // Some payments of a payment_pending run weren't sent: never shown
              // as fully submitted, and paying again sends only the rest.
              const unsent = run.status === 'payment_pending' ? run.unsent_count ?? 0 : 0;
              return (
                <div key={run.id} className="rounded-md border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                    <div>
                      <div className="font-medium text-foreground">{run.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {companyName(run.legal_entity_id) ?? 'No company — created before runs were per company; no more lines can be added'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {run.line_count} line(s) · gross {money(run.gross_total, run.currency)} · WHT {money(run.wht_total, run.currency)} · net {money(run.net_total, run.currency)}
                        {run.void_reason && ` · voided: ${run.void_reason}`}
                      </div>
                      {skipped?.runId === run.id && (
                        <div className="text-xs text-warning" role="status">
                          {skipped.count} chosen line{skipped.count === 1 ? '' : 's'} could not be added — {skipped.count === 1 ? 'it' : 'they'} changed or joined another run.
                        </div>
                      )}
                      {unsent > 0 && (
                        <div className="text-xs text-warning">
                          {unsent} payment{unsent === 1 ? '' : 's'} not sent — the run is held until {unsent === 1 ? 'it is' : 'they are'} sent.
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant={RUN_STATUS[run.status] ?? 'secondary'}>{run.status.replace(/_/g, ' ')}</Badge>
                      {(run.status === 'draft' || run.status === 'validated') && run.legal_entity_id && (
                        <Button size="sm" variant="ghost" disabled={busy === run.id} onClick={() => void openPicker(run)}>Add approved</Button>
                      )}
                      {unsent > 0 && canApprove && (
                        <Button size="sm" variant="outline" disabled={busy === run.id}
                          onClick={() => act(run.id, () => commissionApi.runAction(run.id, 'pay'), 'Unsent payments sent')}>{`Send unsent (${unsent})`}</Button>
                      )}
                      {canDoNext && (
                        <Button size="sm" variant="outline" disabled={busy === run.id}
                          onClick={() => act(run.id, () => commissionApi.runAction(run.id, next!.action), `${next!.label} done`)}>{next!.label}</Button>
                      )}
                      {(run.status === 'approved' || run.status === 'locked' || run.status === 'payment_pending' || run.status === 'paid' || run.status === 'failed') && (
                        <Button size="sm" variant="ghost" onClick={() => showGL(run.id)}>
                          {expanded === run.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} GL
                        </Button>
                      )}
                    </div>
                  </div>
                  {picker?.runId === run.id && (
                    <div className="border-t border-border p-3 text-sm" data-testid="line-picker">
                      {picker.lines === null ? (
                        <p className="text-muted-foreground">Loading approved lines…</p>
                      ) : picker.withheld ? (
                        <p className="text-warning">You can’t see this company’s contractor fees, so you can’t choose lines for this run.</p>
                      ) : picker.lines.length === 0 ? (
                        <p className="text-muted-foreground">No approved lines of this company in {run.currency} are waiting for a run.</p>
                      ) : (
                        <>
                          <div className="mb-2 text-xs text-muted-foreground">Approved lines of this company in {run.currency} that are not on a run. Tick the ones to add.</div>
                          {picker.lines.map((l) => (
                            <label key={l.line_id} className="flex items-center justify-between gap-2 py-1">
                              <span className="flex items-center gap-2">
                                <input type="checkbox" aria-label={`Add ${l.payee_name ?? l.payee_id}`} checked={!!picker.chosen[l.line_id]}
                                  onChange={(e) => setPicker({ ...picker, chosen: { ...picker.chosen, [l.line_id]: e.target.checked } })} />
                                <span className="text-foreground">{l.payee_name ?? l.payee_id}</span>
                                {l.plan_name && <span className="text-muted-foreground">· {l.plan_name}</span>}
                              </span>
                              <span className="tabular-nums">net {money(l.net_amount, l.currency)}</span>
                            </label>
                          ))}
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" disabled={busy === run.id || !Object.values(picker.chosen).some(Boolean)} onClick={() => void addChosen(run)}>
                              {`Add selected (${Object.values(picker.chosen).filter(Boolean).length})`}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setPicker(null)}>Cancel</Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {expanded === run.id && ['approved', 'locked', 'payment_pending', 'paid'].includes(run.status) && (
                    <ContractorAccountingPanel runId={run.id} legalEntityId={run.legal_entity_id} />
                  )}
                  {expanded === run.id && gl && (
                    <div className="border-t border-border p-3 text-sm">
                      <div className="mb-1 font-medium">GL journal {gl.balanced ? <span className="text-success">· balanced</span> : <span className="text-warning">· NOT balanced</span>}</div>
                      {gl.entries.map((e, i) => (
                        <div key={i} className="flex justify-between tabular-nums">
                          <span className="text-muted-foreground">{e.account_code} {e.account}</span>
                          <span>{e.dr_cr} {money(e.amount, gl.currency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
